import React, { Fragment, useEffect, useMemo, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import Select from "react-select";
import {
    Button,
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    IconButton,
    Checkbox
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import { fetchData, deleteData, updateData, activateData, postData } from '../../../apiService';
// -------------------- Component --------------------
const QuotationCreate = () => {
    const history = useHistory();
    const { isAuthenticated, companyId, branchId, userId, logout, token } = useAuth();

    // ---------- Auth guard ----------
    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
        fetchUOM();
        fetchGSTRate();
        fetchTermsAndCondition();
    }, [isAuthenticated, token, logout]);

    // ---------- UI / form states ----------
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    const [searchQuery, setSearchQuery] = useState("");
    const [isFocused, setIsFocused] = useState(false);
    const handleSearch = (event) => setSearchQuery(event.target.value);

    // ---------- Select data ----------
    const [CustomerOptions, setCustomerOptions] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [fetchedCustomerDetails, setFetchedCustomerDetails] = useState([]);

    const [WorkOptions, setWorkOptions] = useState([]);
    const [selectedWork, setSelectedWork] = useState(null);
    const [workId, setWorkId] = useState("");

    const [workitemdetailsOptions, setWorkItemDetailsOptions] = useState([]);
    const [selectedWorkItemDetails, setSelectedWorkItemDetails] = useState(null);
    const [fetchedWorkDetails, setFetchedWorkDetails] = useState([]); // keep raw API rows here

    //GST % LIST 
    const [gstOptions, setGstOptions] = useState([]);
    const [selectedgst, setSelectedGst] = useState(0);

    // IGST checkbox state
    const [isIGST, setIsIGST] = useState(false);

    const fetchGSTRate = async () => {
        try {
            const response = await fetchData('QuotationApi', `/GST/${companyId}`, token); //, token
            if (response.responseCode === '1111') {
                const options = response.response_data.map(gst => ({
                    value: gst.Id,
                    label: gst.Name
                }));
                setGstOptions(options);
            }
        } catch (error) {
            console.error('Error fetching route:', error);
        }
    };

    const handleGSTChange = (selectedOption) => {
        setSelectedGst(selectedOption);

        // Extract GST rate from the selected option
        const newGstRate = selectedOption?.label
            ? (parseFloat(selectedOption.label.replace('%', '')) || 0)
            : 0;

        // Update formData with the new GST rate
        setFormData((prev) => ({
            ...prev,
            GSTRate: newGstRate
        }));

        // Recalculate all existing items with the new GST rate
        const quotationKey = "QuotationDetails";
        setGroupedData((prevData) => {
            const updated = structuredClone(prevData || {}); // deep clone

            if (!updated[quotationKey]) return updated;

            // Loop through all QA sections
            Object.keys(updated[quotationKey]).forEach((qaName) => {
                // Loop through all work groups in each QA section
                Object.keys(updated[quotationKey][qaName]).forEach((workName) => {
                    const items = updated[quotationKey][qaName][workName];

                    // Update each item with new GST rate and recalculate
                    items.forEach((item) => {
                        item.GSTRate = newGstRate;

                        // Recalculate GST amounts based on IGST checkbox
                        const gstAmount = parseFloat(((item.Amount * newGstRate) / 100).toFixed(2));
                        if (isIGST) {
                            item.IGSTAmount = gstAmount;
                            item.CGSTAmount = 0;
                            item.SGSTAmount = 0;
                        } else {
                            item.CGSTAmount = parseFloat((gstAmount / 2).toFixed(2));
                            item.SGSTAmount = parseFloat((gstAmount / 2).toFixed(2));
                            item.IGSTAmount = 0;
                        }
                        item.TotalAmount = parseFloat((item.Amount + gstAmount).toFixed(2));
                    });
                });
            });

            return updated;
        });
    };

    // Handle IGST checkbox change
    const handleIGSTChange = (e) => {
        const checked = e.target.checked;
        setIsIGST(checked);

        // Recalculate all existing items with IGST logic
        const quotationKey = "QuotationDetails";
        setGroupedData((prevData) => {
            const updated = structuredClone(prevData || {}); // deep clone

            if (!updated[quotationKey]) return updated;

            // Loop through all QA sections
            Object.keys(updated[quotationKey]).forEach((qaName) => {
                // Loop through all work groups in each QA section
                Object.keys(updated[quotationKey][qaName]).forEach((workName) => {
                    const items = updated[quotationKey][qaName][workName];

                    // Update each item with IGST calculation
                    items.forEach((item) => {
                        const gstRate = parseFloat(item.GSTRate) || 0;
                        const gstAmount = parseFloat(((item.Amount * gstRate) / 100).toFixed(2));

                        if (checked) {
                            // IGST mode: full GST goes to IGST
                            item.IGSTAmount = gstAmount;
                            item.CGSTAmount = 0;
                            item.SGSTAmount = 0;
                        } else {
                            // Normal mode: split between CGST and SGST
                            item.CGSTAmount = parseFloat((gstAmount / 2).toFixed(2));
                            item.SGSTAmount = parseFloat((gstAmount / 2).toFixed(2));
                            item.IGSTAmount = 0;
                        }
                        item.TotalAmount = parseFloat((item.Amount + gstAmount).toFixed(2));
                    });
                });
            });

            return updated;
        });
    };


    // ---------- Table / grouping ----------
    const [selectedWorkDetails, setSelectedWorkDetails] = useState([]);
    const [groupedData, setGroupedData] = useState({});
    const [expandedGroups, setExpandedGroups] = useState({});

    // ---------- Form data ----------
    const [formData, setFormData] = useState({
        CompanyId: companyId,
        CustomerId: "",
        QuotationDate: new Date().toISOString().slice(0, 10), // yyyy-mm-dd for <input type="date" />
        QuotationNumber: "",
        FinYear: "",
        IsRevised: false,
        Amount: "",
        TaxableAmount: "",
        GSTRate: "0",
        GSTAmount: "",
        CGSTAmount: "",
        SGSTAmount: "",
        IGSTAmount: "",
        RoundOff: "0.00",
        TotalAmount: "",
        GrandTotal: "",
        Remarks: "",
        Description: "",
        HSNCode: "",
        UnitofMeasuresId: "",
        CreatedBy: userId,
        CreatedDate: new Date().toISOString(),
    });

    // ---------- Effects: Initial fetch ----------
    useEffect(() => {
        const init = async () => {
            try {
                // Customers
                const c = await fetchData("QuotationApi", `/Customer/${companyId}`, token);
                if (c?.responseCode === "1111") {
                    setCustomerOptions(
                        c.response_data.map((x) => ({ value: x.Id, label: x.Name }))
                    );
                }

                // Works
                const w = await fetchData("QuotationApi", `/Work/${companyId}`, token);
                if (w?.responseCode === "1111") {
                    setWorkOptions(
                        w.response_data.map((x) => ({ value: x.Id, label: x.Name }))
                    );
                }
            } catch (e) {
                console.error("Init fetch error:", e);
            }
        };
        init();
    }, [companyId, token]);

    // ---------- Handlers: customer ----------
    const handleCustomerChange = async (opt) => {
        setSelectedCustomer(opt);
        setFormData((prev) => ({
            ...prev,
            CreatedBy: userId,
            CompanyId: companyId,
            CustomerId: opt?.value || "",
        }));
        try {
            const res = await fetchData("CustomerApi", `/CustomerDetails/${opt.value}`, token);
            if (res?.responseCode === "1111") setFetchedCustomerDetails(res.response_data);
        } catch (e) {
            console.error("Error fetching Customer details:", e);
        }
    };

    // ---------- Handlers: work & details ----------
    const handleWorkChange = async (opt) => {
        setSelectedWork(opt);
        setWorkId(opt?.value || "");

        if (!opt?.value) {
            setFetchedWorkDetails([]);
            setWorkItemDetailsOptions([]);
            setSelectedWorkItemDetails(null);
            return;
        }

        try {
            // Get WorkDetails for the selected work
            const response = await fetchData("QuotationApi", `/WorkDetails/${opt.value}`, token);
            if (response?.responseCode === "1111") {
                const list = response.response_data || [];
                setFetchedWorkDetails(list); // raw rows for later mapping

                // Options for dropdown
                const options = list.map((wd) => ({
                    value: wd.Id,
                    label: `${wd.WorkItemName} / ${wd.Name} - ${wd.Description || ""}`,
                }));
                setWorkItemDetailsOptions(options);
            }
        } catch (error) {
            console.error("Error fetching WorkDetails:", error);
        }
    };

    const handleWorkItemDetailsChange = (selectedOption) => {
        setSelectedWorkItemDetails(selectedOption);
    };

    // ---------- Totals aggregation ----------
    useEffect(() => {
        const quotationKey = "QuotationDetails";
        const allItems = [];

        // Flatten nested groupedData structure
        if (groupedData[quotationKey]) {
            Object.values(groupedData[quotationKey]).forEach((qaGroup) => {
                Object.values(qaGroup).forEach((workItems) => {
                    allItems.push(...workItems);
                });
            });
        }

        const totals = allItems.reduce(
            (acc, item) => {
                const amt = parseFloat(item.Amount) || 0;
                const taxAmt = parseFloat(item.TaxableAmount) || 0;
                const cgst = parseFloat(item.CGSTAmount) || 0;
                const sgst = parseFloat(item.SGSTAmount) || 0;
                const igst = parseFloat(item.IGSTAmount) || 0;
                const gst = cgst + sgst + igst;
                const total = parseFloat(item.TotalAmount) || 0;

                acc.Amount += amt;
                acc.TaxableAmount += taxAmt;
                acc.CGSTAmount += cgst;
                acc.SGSTAmount += sgst;
                acc.IGSTAmount += igst;
                acc.GSTAmount += gst;
                acc.TotalAmount += total;
                return acc;
            },
            {
                Amount: 0,
                TaxableAmount: 0,
                CGSTAmount: 0,
                SGSTAmount: 0,
                IGSTAmount: 0,
                GSTAmount: 0,
                TotalAmount: 0,
            }
        );

        const roundOff = +(Math.round(totals.TotalAmount) - totals.TotalAmount).toFixed(2);
        const grandTotal = Math.round(totals.TotalAmount);

        setFormData((prev) => ({
            ...prev,
            Amount: totals.Amount.toFixed(2),
            TaxableAmount: totals.TaxableAmount.toFixed(2),
            CGSTAmount: totals.CGSTAmount.toFixed(2),
            SGSTAmount: totals.SGSTAmount.toFixed(2),
            IGSTAmount: totals.IGSTAmount.toFixed(2),
            GSTAmount: totals.GSTAmount.toFixed(2),
            RoundOff: roundOff.toFixed(2),
            TotalAmount: grandTotal.toFixed(2),//totals.TotalAmount.toFixed(2),
            GrandTotal: grandTotal.toFixed(2),
        }));
    }, [groupedData]);

    // ---------- Fetch UOM List ----------
    const [UomOptions, setUomOptions] = useState([]);
    const [selecteduom, setSelecteduom] = useState(null);

    const fetchUOM = async () => {
        try {
            const response = await fetchData('UOMApi', `/${companyId}`, token); //, token
            if (response.responseCode === '1111') {
                const options = response.response_data.map(uom => ({
                    value: uom.Id,
                    label: uom.Name
                }));
                setUomOptions(options);
            }
        } catch (error) {
            console.error('Error fetching route:', error);
        }
    };

    // ============================
    // WORK DETAILS ADD & UOM FIX
    // ============================

    const handleRemarksChange = (e, qaName, workName, index) => {
        const quotationKey = "QuotationDetails";
        const { value } = e.target;

        // ✅ Update groupedData safely - only update the specific item
        setGroupedData((prevData) => {
            const updated = structuredClone(prevData || {}); // deep clone

            if (!updated[quotationKey]) updated[quotationKey] = {};
            if (!updated[quotationKey][qaName]) updated[quotationKey][qaName] = {};
            if (!Array.isArray(updated[quotationKey][qaName][workName]))
                updated[quotationKey][qaName][workName] = [];

            const targetArray = updated[quotationKey][qaName][workName];

            if (targetArray[index]) {
                targetArray[index] = {
                    ...targetArray[index],
                    Remarks: value,
                };
            }

            return updated;
        });
    };

    const ITEM_REMARK_MAX = 750;

    const handleItemRemarkChange = (e, qaName, workName, index) => {
        const quotationKey = "QuotationDetails";
        const { value } = e.target;

        // Block input beyond 750 characters
        if (value.length > ITEM_REMARK_MAX) return;

        setGroupedData((prevData) => {
            const updated = structuredClone(prevData || {});

            if (!updated[quotationKey]) updated[quotationKey] = {};
            if (!updated[quotationKey][qaName]) updated[quotationKey][qaName] = {};
            if (!Array.isArray(updated[quotationKey][qaName][workName]))
                updated[quotationKey][qaName][workName] = [];

            const targetArray = updated[quotationKey][qaName][workName];

            if (targetArray[index]) {
                targetArray[index] = {
                    ...targetArray[index],
                    ItemRemark: value,
                };
            }

            return updated;
        });
    };

    // ==================================
    // TERMS & CONDITIONS
    // ==================================

    // 🔹 States
    const [isModalGrpOpen, setIsModalGrpOpen] = useState(false);
    const [isloading, setLoading] = useState(false);
    const [data, setData] = useState([]);

    // ✅ Toggle modal open/close
    const LoadTermsAndConditions = () => {
        if (!isModalGrpOpen) {
            fetchTermsAndCondition();
        }
        setIsModalGrpOpen(!isModalGrpOpen);
    };

    // ✅ Close modal
    const handleCloseModal = () => {
        setIsModalGrpOpen(false);
    };

    // ✅ Fetch API
    const fetchTermsAndCondition = async () => {
        setLoading(true);
        try {
            const response = await fetchData("TermsAndConditionApi", `/${companyId}`, token);
            if (response.responseCode === "1111") {
                setData(response.response_data || []);
            } else {
                setData([]);
            }
        } catch (error) {
            console.error("Error fetching Terms And Condition:", error);
            if (error.response && error.response.status === 401) {
                logout();
                history.replace(`/login?redirect=${encodeURIComponent(window.location.pathname)}`);
            }
        } finally {
            setLoading(false);
        }
    };

    const filteredData = data; // You can filter it if needed

    // ==========================================
    // TERMS & CONDITIONS / SELECTION BY CHECKBOX
    // ==========================================
    // ✅ local selection state
    const [selectedItems, setSelectedItems] = useState([]);

    // ✅ handle checkbox toggle
    const handleCheckboxChange = (item) => {
        setSelectedItems((prev) => {
            const exists = prev.some((x) => x.Id === item.Id);
            let updated;
            if (exists) {
                // remove if already selected
                updated = prev.filter((x) => x.Id !== item.Id);
            } else {
                // add if new
                updated = [...prev, item];
            }

            // send updated list to parent (for "QA" list)
            onSelectionChange(updated);
            return updated;
        });
    };

    // ✅ check if item is selected
    const isItemSelected = (item) =>
        selectedItems.some((x) => x.Id === item.Id);

    const [termsListQA, setTermsListQA] = useState([]);
    const onSelectionChange = (selected) => {
        setTermsListQA(selected);
    };

    // const [qaChildDetailName, setQaChildDetailName] = useState([]);
    const [qaChildDetailName, setQaChildDetailName] = useState("");
    const quotationKey = "QuotationDetails";

    // ---------- Submit ----------

    const handleAddWork = () => {
        if (!qaChildDetailName || !selectedWork || !selectedWorkItemDetails) return;

        const detailRow = fetchedWorkDetails.find(
            (x) => x.Id === selectedWorkItemDetails.value
        );
        if (!detailRow) return;

        // Extract GST rate from dropdown selection, default to formData.GSTRate or 0
        const gstRate = selectedgst?.label
            ? (parseFloat(selectedgst.label.replace('%', '')) || 0)
            : (parseFloat(formData.GSTRate) || 0);

        const newItem = {
            workId: detailRow.WorkId,
            WorkItemId: detailRow.WorkItemId,
            WorkDetailsId: detailRow.Id,
            WorkName: detailRow.WorkName || selectedWork.label,
            QAChildDetailName: qaChildDetailName.trim(),
            WorkItemName: detailRow.WorkItemName || "",
            Name: detailRow.Name || "",
            UOM: detailRow.UOM || "",
            UnitofMeasuresId: detailRow.UnitofMeasuresId || "",
            Description: detailRow.Description || "",
            Quantity: 1,
            Rate: parseFloat(detailRow.Rate || 0),
            Amount: parseFloat(detailRow.Rate || 0),
            TaxableAmount: parseFloat(detailRow.Rate || 0),
            GSTRate: gstRate,
            CGSTAmount: 0,
            SGSTAmount: 0,
            IGSTAmount: 0,
            TotalAmount: 0,
            Remarks: detailRow.Remarks || "",
        };

        // GST calculation using extracted rate from dropdown
        const gstAmount = parseFloat(
            ((newItem.Amount * gstRate) / 100).toFixed(2)
        );

        if (isIGST) {
            // IGST mode: full GST goes to IGST
            newItem.IGSTAmount = gstAmount;
            newItem.CGSTAmount = 0;
            newItem.SGSTAmount = 0;
        } else {
            // Normal mode: split between CGST and SGST
            newItem.CGSTAmount = parseFloat((gstAmount / 2).toFixed(2));
            newItem.SGSTAmount = parseFloat((gstAmount / 2).toFixed(2));
            newItem.IGSTAmount = 0;
        }
        newItem.TotalAmount = parseFloat((newItem.Amount + gstAmount).toFixed(2));

        // === Update nested groupedData ===
        setGroupedData((prev) => {
            const updated = { ...prev };
            if (!updated[quotationKey]) updated[quotationKey] = {};
            if (!updated[quotationKey][qaChildDetailName])
                updated[quotationKey][qaChildDetailName] = {};
            if (!updated[quotationKey][qaChildDetailName][newItem.WorkName])
                updated[quotationKey][qaChildDetailName][newItem.WorkName] = [];

            // Check duplicate inside same QA + Work group
            const allItemsInQA = Object.values(updated[quotationKey][qaChildDetailName] || {}).flat();

            const exists = allItemsInQA.some(
                (x) =>
                    x.QAChildDetailName === newItem.QAChildDetailName &&
                    x.WorkItemName === newItem.WorkItemName &&
                    x.Name === newItem.Name &&
                    x.Description === newItem.Description
            );

            if (exists) {
                alert(
                    `This work item already exists in QA section "${newItem.QAChildDetailName}" with the same details!`
                );
                return prev;
            }

            // ✅ Safe to add new item
            updated[quotationKey][qaChildDetailName][newItem.WorkName] = [
                ...updated[quotationKey][qaChildDetailName][newItem.WorkName],
                newItem,
            ];


            return updated;
        });

        // auto expand only this group
        setExpandedGroups((prev) => ({
            ...prev,
            [`${qaChildDetailName}_${newItem.WorkName}`]: true,
        }));

        // reset detail select only
        setSelectedWorkItemDetails(null);
    };

    const toggleGroup = (qaName, workName) => {
        const key = `${qaName}_${workName}`;
        setExpandedGroups((prev) => ({
            ...prev,
            [key]: !prev[key],
        }));
    };

    const deleteItem = (qaName, workName, index) => {
        if (window.confirm("Delete this item?")) {
            setGroupedData((prev) => {
                const updated = { ...prev };
                updated[quotationKey][qaName][workName].splice(index, 1);
                if (updated[quotationKey][qaName][workName].length === 0)
                    delete updated[quotationKey][qaName][workName];
                if (Object.keys(updated[quotationKey][qaName]).length === 0)
                    delete updated[quotationKey][qaName];
                return updated;
            });
        }
    };

    const filteredGroupedData = groupedData[quotationKey] || {};

    const handleCalculation = (e, qaName, workName, index, field) => {
        const quotationKey = "QuotationDetails";
        const raw = e.target.value;
        const value = raw === "" ? "" : parseFloat(raw);

        // ✅ Update groupedData safely
        setGroupedData((prevData) => {
            const updated = structuredClone(prevData || {}); // deep clone

            if (!updated[quotationKey]) updated[quotationKey] = {};
            if (!updated[quotationKey][qaName]) updated[quotationKey][qaName] = {};
            if (!Array.isArray(updated[quotationKey][qaName][workName]))
                updated[quotationKey][qaName][workName] = [];

            const targetArray = updated[quotationKey][qaName][workName];

            if (targetArray[index]) {
                const item = { ...targetArray[index] };
                item[field] = value;

                // Recalculate amounts
                const qty = parseFloat(item.Quantity) || 0;
                const rate = parseFloat(item.Rate) || 0;
                item.Amount = parseFloat((qty * rate).toFixed(2));
                item.TaxableAmount = item.Amount;

                const gstRate = parseFloat(item.GSTRate) || 0;
                const gstAmount = parseFloat(((item.Amount * gstRate) / 100).toFixed(2));

                if (isIGST) {
                    // IGST mode: full GST goes to IGST
                    item.IGSTAmount = gstAmount;
                    item.CGSTAmount = 0;
                    item.SGSTAmount = 0;
                } else {
                    // Normal mode: split between CGST and SGST
                    item.CGSTAmount = parseFloat((gstAmount / 2).toFixed(2));
                    item.SGSTAmount = parseFloat((gstAmount / 2).toFixed(2));
                    item.IGSTAmount = 0;
                }
                item.TotalAmount = parseFloat((item.Amount + gstAmount).toFixed(2));

                targetArray[index] = item;
            }

            return updated;
        });
    };

    const handleUOMChange = (selectedOption, qaName, workName, index) => {
        const quotationKey = "QuotationDetails";

        // ✅ Update groupedData safely - only update the specific item
        setGroupedData((prevData) => {
            const updated = structuredClone(prevData || {}); // deep clone

            if (!updated[quotationKey]) updated[quotationKey] = {};
            if (!updated[quotationKey][qaName]) updated[quotationKey][qaName] = {};
            if (!Array.isArray(updated[quotationKey][qaName][workName]))
                updated[quotationKey][qaName][workName] = [];

            const targetArray = updated[quotationKey][qaName][workName];

            if (targetArray[index]) {
                targetArray[index] = {
                    ...targetArray[index],
                    UnitofMeasuresId: selectedOption?.value ?? selectedWorkItemDetails.UnitofMeasuresId,
                    UOM: selectedOption?.label ?? selectedWorkItemDetails.UOM ?? "",
                };
            }

            return updated;
        });
    };

    // const handleSubmit = async (event) => {
    //     event.preventDefault();
    //     setIsLoading(true);

    //     if (!formData.QuotationNumber) {
    //         setErrorMessage("QA Number is required !");
    //         setIsLoading(false);
    //         return;
    //     }
    //     if (!formData.CustomerId) {
    //         setErrorMessage("Customer is required !");
    //         setIsLoading(false);
    //         return;
    //     }

    //     // ✅ Flatten the nested grouped data into a single array of details
    //     const quotationKey = "QuotationDetails";
    //     const updatedQuotationDetails = [];

    //     if (groupedData[quotationKey]) {
    //         Object.values(groupedData[quotationKey]).forEach((qaGroup) => {
    //             Object.values(qaGroup).forEach((workItems) => {
    //                 updatedQuotationDetails.push(...workItems);
    //             });
    //         });
    //     }

    //     const quotationVM = {
    //         Quotation: {
    //             ...formData,
    //             CreatedBy: userId,
    //             CompanyId: companyId,
    //             IsRevised: false,
    //         },
    //         QuotationDetails: updatedQuotationDetails, // ✅ use latest UOM, etc.
    //         QATermsAndCondition: termsListQA,
    //     };

    //     try {
    //         await postData("QuotationApi", quotationVM, token);
    //         history.push("/QuotationIndex");
    //     } catch (error) {
    //         console.error("Error creating quotation:", error);
    //         setErrorMessage("Something went wrong while saving!");
    //     } finally {
    //         setIsLoading(false);
    //     }
    // };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setIsLoading(true);

        if (!formData.QuotationNumber) {
            setErrorMessage("QA Number is required !");
            setIsLoading(false);
            return;
        }
        if (!formData.CustomerId) {
            setErrorMessage("Customer is required !");
            setIsLoading(false);
            return;
        }

        if (!formData.UnitofMeasuresId) {
            setErrorMessage("UOM is required !");
            setIsLoading(false);
            return;
        }

        //  if (!formData.GSTRate) {
        //     setErrorMessage("GSTRate is required !");
        //     setIsLoading(false);
        //     return;
        // }

        // ✅ Validate ItemRemark length
        const quotationKey = "QuotationDetails";
        if (groupedData[quotationKey]) {
            for (const qaGroup of Object.values(groupedData[quotationKey])) {
                for (const workItems of Object.values(qaGroup)) {
                    for (const item of workItems) {
                        if ((item.ItemRemark || "").length > ITEM_REMARK_MAX) {
                            setErrorMessage(`Item Remark must be ${ITEM_REMARK_MAX} characters or less. Please correct before saving.`);
                            setIsLoading(false);
                            return;
                        }
                    }
                }
            }
        }

        // ✅ Flatten grouped data
        const updatedQuotationDetails = [];

        if (groupedData[quotationKey]) {
            Object.values(groupedData[quotationKey]).forEach((qaGroup) => {
                Object.values(qaGroup).forEach((workItems) => {
                    updatedQuotationDetails.push(...workItems);
                });
            });
        }

        // ✅ Transform selected QA terms into saveable objects
        const formattedTerms = termsListQA.map((term) => ({
            Id: 0, // EF auto-generates
            TermsAndConditionId: term.Id, // ✅ This links to the master
            Name: term.Name || term.Name || "", // optional
            IsActive: true,
        }));

        const quotationVM = {
            Quotation: {
                ...formData,
                CreatedBy: userId,
                CompanyId: companyId,
                IsRevised: false,
                IsType: isIGST, // Add IGST flag (IsType field in database)
                IsActive: true,
                GSTRate: selectedgst?.label
                    ? (parseFloat(selectedgst.label.replace('%', '')) || 0)
                    : (parseFloat(formData.GSTRate) || 0)
            },
            QuotationDetails: updatedQuotationDetails,
            QATermsAndCondition: formattedTerms, // ✅ Send mapped terms
        };

        try {
            await postData("QuotationApi", quotationVM, token);
            history.push("/QuotationIndex");
        } catch (error) {
            console.error("Error creating quotation:", error);
            setErrorMessage("Something went wrong while saving!");
        } finally {
            setIsLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleInputChange = handleChange; // keep alias as in your code

    // -------------------- UI (your return, minimally adjusted) --------------------

    return (
        <div
            style={{
                fontFamily: "Segoe UI, sans-serif",
                fontSize: "12px",
                width: "100%",
                padding: "5px",
                marginTop: '-15px'
            }}
        >

            {/* Header */}
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    borderBottom: "1px solid #ccc",
                    paddingBottom: "6px",
                    marginBottom: "12px"
                }}
            >
                <h6 className="uppercase tracking-wide font-serif text-black mt-2">
                    Quotation Create
                </h6>

                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "300px repeat(3, 1fr)",
                        gap: "10px",
                        marginBottom: "12px",
                        alignItems: "end",
                    }}
                >
                    <div style={{ display: "flex", alignItems: "center", width: "300px" }}>
                        {/* Label + Type */}
                        <label style={{
                            fontSize: "11px",
                            fontWeight: "600",
                            marginRight: "8px",
                            whiteSpace: "nowrap",
                            color: 'red'
                        }}>
                            Customer
                            <span style={{ color: "black", marginLeft: "8px" }}>
                                (GST No: {fetchedCustomerDetails?.GSTNo || "-"})
                            </span>
                        </label>

                        {/* Input */}
                        <div style={{ flexGrow: 1 }}>
                            <Select
                                value={selectedCustomer}
                                onChange={handleCustomerChange}
                                options={CustomerOptions}
                                placeholder="Customer"
                                isClearable
                                styles={{
                                    control: (base) => ({
                                        ...base,
                                        minHeight: "30px",
                                        height: "30px",
                                        fontSize: "14px",
                                        width: "300px", // updated width
                                    }),
                                    valueContainer: (base) => ({
                                        ...base,
                                        height: "30px",
                                        padding: "0 6px",
                                    }),
                                    indicatorsContainer: (base) => ({
                                        ...base,
                                        height: "30px",
                                    }),
                                    menu: (provided) => ({
                                        ...provided,
                                        zIndex: 1301,
                                        fontSize: "14px",
                                    }),
                                }}
                            />
                        </div>
                    </div>

                </div>

                <div className="flex items-center gap-3 mt-2 md:mt-0" style={{ flexShrink: 0 }}>
                    {/* Terms & Conditions Button */}
                    <>
                        {/* Terms & Conditions Button */}
                        <Button
                            variant="contained"
                            onClick={LoadTermsAndConditions}
                            style={{
                                height: "25px",
                                minWidth: "80px",
                                borderRadius: "6px",
                                backgroundColor: "#885b09ff",
                                color: "#FFFFFF",
                                fontWeight: 600,
                                fontSize: "13px",
                                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                                textTransform: "none",
                                transition: "background-color 0.2s, box-shadow 0.2s",
                            }}
                            onMouseOver={(e) => (e.currentTarget.style.backgroundColor = "#e09b4dff")}
                            onMouseOut={(e) => (e.currentTarget.style.backgroundColor = "#885b09ff")}
                        >
                            {isLoading ? <CircularProgress size={20} color="inherit" /> : "Terms & Conditions"}
                        </Button>

                        {/* ✅ Modal */}
                        {isModalGrpOpen && (
                            <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/50">
                                <div
                                    className="bg-white p-6 rounded-lg shadow-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto animate-zoomIn"
                                >
                                    {/* Header with Close Button */}
                                    <div className="flex justify-between items-center mb-4 border-b pb-2">
                                        <h6 className="uppercase tracking-wide font-serif text-black mt-2">
                                            Terms & Conditions
                                        </h6>
                                        <IconButton onClick={handleCloseModal}>
                                            <CloseIcon />
                                        </IconButton>
                                    </div>

                                    {/* Table Section */}
                                    <div
                                        className="w-full"
                                        style={{ maxHeight: "520px", overflowY: "auto", width: "100%", marginTop: '-15px' }}
                                    >
                                        <TableContainer
                                            component={Paper}
                                            sx={{
                                                maxHeight: 400, // 👈 set a fixed height to enable scrolling
                                                border: "1px solid #f4dfdf",
                                            }}
                                        >
                                            <Table stickyHeader>
                                                <TableHead>
                                                    <TableRow sx={{ backgroundColor: "#0f3460" }}>
                                                        <TableCell sx={{ color: "white", fontWeight: "bold", padding: '3px', backgroundColor: "#071b33ff", fontSize: "12px" }}></TableCell>
                                                        <TableCell sx={{ color: "white", fontWeight: "bold", padding: '3px', backgroundColor: "#071b33ff", fontSize: "12px" }}>Sl.No</TableCell>
                                                        <TableCell sx={{ color: "white", fontWeight: "bold", padding: '3px', backgroundColor: "#071b33ff", fontSize: "12px" }}>Name</TableCell>
                                                    </TableRow>
                                                </TableHead>

                                                <TableBody>
                                                    {filteredData && filteredData.length > 0 ? (
                                                        filteredData.map((item, index) => (
                                                            <TableRow
                                                                key={item.Id || index}
                                                                sx={{
                                                                    "&:hover": { backgroundColor: "#F1E7E7" },
                                                                    transition: "background-color 0.3s ease-in-out",
                                                                    cursor: "pointer",
                                                                }}
                                                            >
                                                                {/* ✅ Checkbox column */}
                                                                <TableCell
                                                                    sx={{
                                                                        padding: "2px",
                                                                        width: "5%",
                                                                        borderRight: "1px solid #f4dfdf",
                                                                    }}
                                                                >
                                                                    <Checkbox
                                                                        checked={isItemSelected(item)}
                                                                        onChange={() => handleCheckboxChange(item)}
                                                                        color="primary"
                                                                        size="small"
                                                                    />
                                                                </TableCell>

                                                                {/* Serial No */}
                                                                <TableCell
                                                                    sx={{
                                                                        padding: "4px",
                                                                        borderRight: "1px solid #f4dfdf",
                                                                        fontSize: "12px",
                                                                        width: "10%",
                                                                    }}
                                                                >
                                                                    {index + 1}
                                                                </TableCell>

                                                                {/* Name */}
                                                                <TableCell
                                                                    sx={{
                                                                        padding: "4px",
                                                                        fontSize: "12px",
                                                                    }}
                                                                >
                                                                    {item.Name}
                                                                </TableCell>
                                                            </TableRow>
                                                        ))
                                                    ) : (
                                                        <TableRow>
                                                            <TableCell colSpan={3} align="center" sx={{ padding: "20px" }}>
                                                                {isLoading ? "Loading..." : "No terms found."}
                                                            </TableCell>
                                                        </TableRow>
                                                    )}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </div>
                                </div>
                            </div>
                        )}
                    </>

                    {/* Save Button */}
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={isLoading}
                        style={{
                            height: "25px",
                            minWidth: "80px",
                            borderRadius: "6px",
                            backgroundColor: "#1E3A8A", // deep ERP-blue
                            color: "#FFFFFF",
                            fontWeight: 600,
                            fontSize: "13px",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                            textTransform: "none",
                            transition: "background-color 0.2s, box-shadow 0.2s",
                        }}
                        onMouseOver={(e) => (e.currentTarget.style.backgroundColor = "#3B82F6")}
                        onMouseOut={(e) => (e.currentTarget.style.backgroundColor = "#1E3A8A")}
                    >
                        {isLoading ? <CircularProgress size={20} color="inherit" /> : "Save"}
                    </Button>

                    {/* Back To List Button */}
                    <Button
                        variant="contained"
                        component={Link}
                        to="/QuotationIndex"
                        className="linkcolor"
                        onClick={() => localStorage.setItem("shouldReload", "true")}
                        style={{
                            height: "25px",
                            minWidth: "90px",
                            borderRadius: "6px",
                            backgroundColor: "#F3F4F6", // light gray ERP style
                            color: "#1E3A8A",
                            fontWeight: 600,
                            fontSize: "13px",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                            textTransform: "none",
                            transition: "background-color 0.2s, box-shadow 0.2s",
                        }}
                        onMouseOver={(e) => (e.currentTarget.style.backgroundColor = "#E5E7EB")}
                        onMouseOut={(e) => (e.currentTarget.style.backgroundColor = "#F3F4F6")}
                    >
                        Back To List
                    </Button>
                </div>

            </div>
            {errorMessage && <p className="text-red-500">{errorMessage}</p>}

            {/* <div className="grid grid-cols-12 gap-1 p-4 mb-3 rounded-lg bg-gray-100 -mt-2" style={{ marginTop: '-20px', background: '#F1F0E9' }}> */}
            <div
                style={{
                    display: "grid",
                    // gridTemplateColumns: "250px repeat(7, 1fr)", 
                    gridTemplateColumns: "repeat(10, 1fr)",
                    gap: "10px",
                    marginBottom: "12px",
                    alignItems: "end" // ✅ aligns all inputs properly
                }}
            >
                <div>
                    <label style={{ fontSize: "11px", fontWeight: "600" }}>QA Date</label>
                    <input
                        type="date"
                        name="QuotationDate"
                        value={formData.QuotationDate}
                        onChange={handleInputChange}
                        style={{
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                            fontSize: "12px",
                            height: "28px",
                            width: "100%",
                            padding: "0 6px"
                        }}
                    />
                </div>

                <div>
                    <label style={{ fontSize: "11px", fontWeight: "600", color: 'red' }}>
                        QA No
                    </label>
                    <input
                        type="text"
                        name="QuotationNumber"
                        value={formData.QuotationNumber}
                        onChange={handleChange}
                        style={{
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                            fontSize: "12px",
                            height: "28px",
                            width: "100%",
                            padding: "0 6px"
                        }}
                    />
                </div>

                <div>
                    <label style={{ fontSize: "11px", fontWeight: "600" }}>Amount</label>
                    <input
                        type="number"
                        name="Amount"
                        value={formData.Amount}
                        onChange={handleChange}
                        disabled
                        style={{
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                            fontSize: "12px",
                            height: "28px",
                            width: "100%",
                            padding: "0 6px",
                            textAlign: "right"
                        }}
                    />
                </div>

                <div>
                    <label style={{ fontSize: "11px", fontWeight: "600" }}>TaxableAmount</label>
                    <input
                        type="number"
                        name="TaxableAmount"
                        value={formData.TaxableAmount}
                        onChange={handleChange}
                        disabled
                        style={{
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                            fontSize: "12px",
                            height: "28px",
                            width: "100%",
                            padding: "0 6px",
                            textAlign: "right"
                        }}
                    />
                </div>

                <div>
                    <label style={{ fontSize: "11px", fontWeight: "600" }}>CGSTAmount</label>
                    <input
                        type="number"
                        name="CGSTAmount"
                        value={formData.CGSTAmount}
                        onChange={handleChange}
                        disabled
                        style={{
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                            fontSize: "12px",
                            height: "28px",
                            width: "100%",
                            padding: "0 6px",
                            textAlign: "right"
                        }}
                    />
                </div>

                <div>
                    <label style={{ fontSize: "11px", fontWeight: "600" }}>SGSTAmount</label>
                    <input
                        type="number"
                        name="SGSTAmount"
                        value={formData.SGSTAmount}
                        onChange={handleChange}
                        disabled
                        style={{
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                            fontSize: "12px",
                            height: "28px",
                            width: "100%",
                            padding: "0 6px",
                            textAlign: "right"
                        }}
                    />
                </div>

                <div>
                    <label style={{ fontSize: "11px", fontWeight: "600" }}>IGSTAmount</label>
                    <input
                        type="number"
                        name="IGSTAmount"
                        value={formData.IGSTAmount}
                        onChange={handleChange}
                        disabled
                        style={{
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                            fontSize: "12px",
                            height: "28px",
                            width: "100%",
                            padding: "0 6px",
                            textAlign: "right"
                        }}
                    />
                </div>

                <div>
                    <label style={{ fontSize: "11px", fontWeight: "600" }}>RoundOff</label>
                    <input
                        type="number"
                        name="RoundOff"
                        value={formData.RoundOff}
                        onChange={handleChange}
                        disabled
                        style={{
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                            fontSize: "12px",
                            height: "28px",
                            width: "100%",
                            padding: "0 6px",
                            textAlign: "right"
                        }}
                    />
                </div>

                <div>
                    <label style={{ fontSize: "11px", fontWeight: "600" }}>TotalAmount</label>
                    <input
                        type="number"
                        name="TotalAmount"
                        value={formData.TotalAmount}
                        onChange={handleChange}
                        disabled
                        style={{
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                            fontSize: "12px",
                            height: "28px",
                            width: "100%",
                            padding: "0 6px",
                            textAlign: "right"
                        }}
                    />
                </div>

                <div>
                    <label style={{ fontSize: "11px", fontWeight: "600" }}>GrandTotal</label>
                    <input
                        type="number"
                        name="GrandTotal"
                        value={formData.GrandTotal}
                        onChange={handleChange}
                        disabled
                        style={{
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                            fontSize: "12px",
                            height: "28px",
                            width: "100%",
                            padding: "0 6px",
                            textAlign: "right"
                        }}
                    />
                </div>

                {/* ---------------- Row 3 ---------------- */}
                {/* Remarks */}

                <div className="col-span-12 md:col-span-2">
                    <label style={{ fontSize: "11px", fontWeight: "600" }}>Work Name</label>
                    <input
                        type="text"
                        name="WorkName"
                        value={formData.WorkName}
                        onChange={handleChange}
                        placeholder="Enter Work Name"
                        style={{
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                            fontSize: "12px",
                            height: "28px",
                            width: "100%",
                            padding: "0 6px",
                        }}
                    />
                </div>

                <div className="col-span-12 md:col-span-2">
                    <label style={{ fontSize: "11px", fontWeight: "600" }}>Bill Header</label>
                    <input
                        type="text"
                        name="Description"
                        value={formData.Description}
                        onChange={handleChange}
                        placeholder="Enter Description"
                        style={{
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                            fontSize: "12px",
                            height: "28px",
                            width: "100%",
                            padding: "0 6px",
                        }}
                    />
                </div>

                <div className="col-span-12 md:col-span-3">
                    <label style={{ fontSize: "11px", fontWeight: "600" }}>Remarks</label>
                    <input
                        type="text"
                        name="Remarks"
                        value={formData.Remarks}
                        onChange={handleChange}
                        placeholder="Enter Remarks"
                        style={{
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                            fontSize: "12px",
                            height: "28px",
                            width: "100%",
                            padding: "0 6px",
                        }}
                    />
                </div>

                <div className="col-span-12 md:col-span-1">
                    <label style={{ fontSize: "11px", fontWeight: "600" }}>HSN Code</label>
                    <input
                        type="text"
                        name="HSNCode"
                        value={formData.HSNCode || ''}
                        onChange={handleChange}
                        placeholder="HSN"
                        style={{
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                            fontSize: "12px",
                            height: "28px",
                            width: "100%",
                            padding: "0 6px",
                        }}
                    />
                </div>

                <div className="col-span-12 md:col-span-1">
                    <label style={{ fontSize: "11px", fontWeight: "600" }}>UOM</label>
                    <Select
                        value={selecteduom}
                        onChange={(selectedOption) => {
                            setSelecteduom(selectedOption);
                            setFormData((prev) => ({
                                ...prev,
                                UnitofMeasuresId: selectedOption?.value || "",
                            }));
                        }}
                        options={UomOptions}
                        placeholder="UOM"
                        isSearchable
                        styles={{
                            control: (base) => ({
                                ...base,
                                minHeight: "30px",
                                height: "30px",
                                fontSize: "14px",
                            }),
                            valueContainer: (base) => ({
                                ...base,
                                height: "30px",
                                padding: "0 6px",
                            }),
                            indicatorsContainer: (base) => ({
                                ...base,
                                height: "30px",
                            }),
                            menu: (provided) => ({
                                ...provided,
                                zIndex: 1301,
                                fontSize: "14px",
                            }),
                        }}
                    />
                </div>

                {/* GST Rate Dropdown */}
                <div className="col-span-12 md:col-span-1">
                    <label style={{ fontSize: "11px", fontWeight: "600" }}>GST %</label>
                    <Select
                        value={selectedgst}
                        onChange={handleGSTChange}
                        options={gstOptions}
                        placeholder="GST %"
                        isSearchable
                        styles={{
                            control: (base) => ({
                                ...base,
                                minHeight: "30px",
                                height: "30px",
                                fontSize: "14px",
                            }),
                            valueContainer: (base) => ({
                                ...base,
                                height: "30px",
                                padding: "0 6px",
                            }),
                            indicatorsContainer: (base) => ({
                                ...base,
                                height: "30px",
                            }),
                            menu: (provided) => ({
                                ...provided,
                                zIndex: 1301,
                                fontSize: "14px",
                            }),
                        }}
                    />
                </div>




            </div>

            {/* Second Row - QA Detail Name, Work, Details, ADD Button */}
            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(12, 1fr)",
                    gap: "10px",
                    marginBottom: "12px",
                    alignItems: "end"
                }}
            >
                {/* IGST Checkbox */}
                <div className="col-span-12 md:col-span-1" style={{ display: "flex", alignItems: "center", paddingTop: "20px" }}>
                    <Checkbox
                        checked={isIGST}
                        onChange={handleIGSTChange}
                        color="primary"
                        size="small"
                        style={{ padding: "0 4px" }}
                    />
                    <label style={{ fontSize: "11px", fontWeight: "600", marginLeft: "4px" }}>Is IGST?</label>
                </div>

                <div className="col-span-12 md:col-span-2">
                    <label style={{ fontSize: "11px", fontWeight: "600" }}>
                        QA Detail Name
                    </label>
                    <input
                        type="text"
                        value={qaChildDetailName}
                        onChange={(e) => setQaChildDetailName(e.target.value)}
                        placeholder="Enter QA Detail Name"
                        style={{
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                            fontSize: "12px",
                            height: "28px",
                            width: "100%",
                            padding: "0 6px",
                        }}
                    />
                </div>

                <div className="col-span-12 md:col-span-2">
                    <label style={{ fontSize: "11px", fontWeight: "600" }}>
                        Work<span style={{ color: 'red' }}>*</span>
                    </label>
                    <Select
                        value={selectedWork}
                        onChange={handleWorkChange}
                        options={WorkOptions}
                        placeholder="Work"
                        isSearchable
                        styles={{
                            control: (base) => ({
                                ...base,
                                minHeight: "30px",
                                height: "30px",
                                fontSize: "14px",
                            }),
                            valueContainer: (base) => ({
                                ...base,
                                height: "30px",
                                padding: "0 6px",
                            }),
                            indicatorsContainer: (base) => ({
                                ...base,
                                height: "30px",
                            }),
                            menu: (provided) => ({
                                ...provided,
                                zIndex: 1301,
                                fontSize: "14px",
                            }),
                        }}
                    />
                </div>

                <div className="col-span-12 md:col-span-6">
                    <label style={{ fontSize: "11px", fontWeight: "600" }}>
                        Details
                    </label>
                    <div className="flex items-center gap-2">
                        <div className="w-full">
                            <Select
                                value={selectedWorkItemDetails}
                                onChange={handleWorkItemDetailsChange}
                                options={workitemdetailsOptions}
                                placeholder="Work Details"
                                isSearchable
                                styles={{
                                    control: (base) => ({
                                        ...base,
                                        minHeight: "30px",
                                        height: "30px",
                                        fontSize: "14px",
                                    }),
                                    valueContainer: (base) => ({
                                        ...base,
                                        height: "30px",
                                        padding: "0 6px",
                                    }),
                                    indicatorsContainer: (base) => ({
                                        ...base,
                                        height: "30px",
                                    }),
                                    menu: (provided) => ({
                                        ...provided,
                                        zIndex: 1301,
                                        fontSize: "14px",
                                    }),
                                }}
                            />
                        </div>

                        <button
                            onClick={handleAddWork}
                            className="w-20 h-8 text-[11px] font-bold text-white rounded shadow transition-transform duration-150 ease-in-out"
                            style={{ backgroundColor: "#2294a5ff" }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = "scale(1.04)";
                                e.currentTarget.style.backgroundColor = "#224f02ff";
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = "scale(1)";
                                e.currentTarget.style.backgroundColor = "#2294a5ff";
                            }}
                        >
                            <span style={{ fontSize: '12.5px' }}>ADD</span>
                        </button>
                    </div>
                </div>

            </div>

            {/* Grouped Table */}

            {Object.keys(filteredGroupedData).length > 0 && (
                <div
                    className="border rounded bg-white"
                    style={{ maxHeight: "420px", overflowY: "auto", overflowX: "auto", width: "100%" }}
                >
                    <table
                        style={{
                            minWidth: "1700px",
                            width: "100%",
                            borderCollapse: "collapse",
                            fontSize: "13px",
                            fontFamily: "monospace",
                            tableLayout: "fixed",
                        }}
                    >
                        <thead
                            style={{
                                backgroundColor: "#1F2A38",
                                color: "#FFFFFF",
                                position: "sticky",
                                top: 0,
                                zIndex: 2,
                            }}
                        >
                            <tr>
                                <th style={{ fontSize: "11px", padding: "4px", textAlign: "left", width: "60px", minWidth: "60px", position: "sticky", left: "0", backgroundColor: "#1F2A38", zIndex: "3" }}>Sl.No</th>
                                <th style={{ fontSize: "11px", padding: "4px", textAlign: "left", width: "400px", minWidth: "400px", position: "sticky", left: "60px", backgroundColor: "#1F2A38", zIndex: "3" }}>Description</th>
                                <th style={{ fontSize: "11px", padding: "4px", textAlign: "left", width: "100px", minWidth: "100px" }}>UOM</th>
                                <th style={{ fontSize: "11px", padding: "4px", textAlign: "right", width: "100px", minWidth: "100px" }}>Qty</th>
                                <th style={{ fontSize: "11px", padding: "4px", textAlign: "right", width: "120px", minWidth: "120px" }}>Rate</th>
                                <th style={{ fontSize: "11px", padding: "4px", textAlign: "right", width: "120px", minWidth: "120px" }}>Amount</th>
                                <th style={{ fontSize: "11px", padding: "4px", textAlign: "right", width: "120px", minWidth: "120px" }}>Taxable</th>
                                <th style={{ fontSize: "11px", padding: "4px", textAlign: "right", width: "120px", minWidth: "120px" }}>GST ₹</th>
                                <th style={{ fontSize: "11px", padding: "4px", textAlign: "right", width: "120px", minWidth: "120px" }}>Total</th>
                                <th style={{ fontSize: "11px", padding: "4px", textAlign: "left", width: "180px", minWidth: "180px" }}>Remarks</th>
                                <th style={{ fontSize: "11px", padding: "4px", textAlign: "left", width: "180px", minWidth: "180px" }}>Item Remark</th>
                            </tr>
                        </thead>

                        <tbody>
                            {Object.entries(filteredGroupedData).map(([qaName, qaGroup]) => (
                                <React.Fragment key={qaName}>
                                    {/* QA HEADER */}
                                    <tr style={{ backgroundColor: "#D7D3BF", textAlign: 'center' }}>
                                        <td
                                            colSpan={11}
                                            style={{
                                                fontWeight: "bold",
                                                fontSize: "13px",
                                                textTransform: "uppercase",
                                            }}
                                        >
                                            {qaName}
                                        </td>
                                    </tr>

                                    {/* WORK GROUPS */}
                                    {Object.entries(qaGroup).map(([workName, works]) => (
                                        <React.Fragment key={workName}>

                                            {/* Work Header Row */}
                                            <tr
                                                onClick={() => toggleGroup(qaName, workName)}
                                                style={{
                                                    backgroundColor: "#f3ecd9",
                                                    cursor: "pointer",
                                                    textTransform: 'uppercase',
                                                }}
                                            >
                                                <td colSpan={11} style={{ fontWeight: "bold" }}>
                                                    {workName}{" "}
                                                    <span style={{ float: "right" }}>
                                                        {expandedGroups[`${qaName}_${workName}`] ? "▲" : "▼"}
                                                    </span>
                                                </td>
                                            </tr>

                                            {/* Work Items */}
                                            {expandedGroups[`${qaName}_${workName}`] &&
                                                works.map((item, index) => (
                                                    <tr
                                                        key={`${qaName}-${workName}-${index}`}
                                                        style={{
                                                            backgroundColor:
                                                                index % 2 === 0 ? "#F7FAFF" : "#FFFFFF",
                                                            borderBottom: "1px solid #E0E0E0",
                                                        }}
                                                    >
                                                        {/* Sl.No + Delete */}
                                                        <td
                                                            style={{
                                                                fontSize: "12px",
                                                                fontWeight: "bold",
                                                                padding: "3px",
                                                                width: "60px",
                                                                position: "sticky",
                                                                left: "0",
                                                                backgroundColor: index % 2 === 0 ? "#F7FAFF" : "#FFFFFF",
                                                                zIndex: "1",
                                                            }}
                                                        >
                                                            {index + 1}
                                                            <Tooltip title="Delete Item">
                                                                <DeleteIcon
                                                                    style={{
                                                                        color: "brown",
                                                                        cursor: "pointer",
                                                                        marginLeft: "10px",
                                                                        fontSize: "17px",
                                                                    }}
                                                                    onClick={() =>
                                                                        deleteItem(qaName, workName, index)
                                                                    }
                                                                />
                                                            </Tooltip>
                                                        </td>

                                                        {/* Description */}
                                                        <td
                                                            style={{
                                                                fontSize: "11px",
                                                                padding: "3px",
                                                                width: "400px",
                                                                borderRight: "1px solid #E0E0E0",
                                                                position: "sticky",
                                                                left: "60px",
                                                                backgroundColor: index % 2 === 0 ? "#F7FAFF" : "#FFFFFF",
                                                                zIndex: "1",
                                                            }}
                                                        >
                                                            <span style={{ color: "brown" }}>
                                                                {item.WorkItemName}
                                                            </span>{" "}
                                                            /{" "}
                                                            <span style={{ color: "blue" }}>{item.Name}</span> /{" "}
                                                            {item.Description}
                                                        </td>

                                                        {/* UOM Dropdown */}
                                                        <td
                                                            style={{
                                                                fontSize: "11px",
                                                                fontWeight: "bold",
                                                                padding: "3px",
                                                                width: "8%",
                                                            }}
                                                        >
                                                            <Select
                                                                value={
                                                                    item.UOM ? { value: item.UnitofMeasuresId, label: item.UOM } : null
                                                                }
                                                                onChange={(selectedOption) =>
                                                                    handleUOMChange(selectedOption, qaName, workName, index)
                                                                }
                                                                options={UomOptions}
                                                                placeholder="UOM"
                                                                isSearchable
                                                                menuPortalTarget={document.body}
                                                                styles={{
                                                                    control: (base) => ({
                                                                        ...base,
                                                                        minHeight: "25px",
                                                                        height: "25px",
                                                                        fontSize: "12px",
                                                                    }),
                                                                    valueContainer: (base) => ({
                                                                        ...base,
                                                                        height: "25px",
                                                                        padding: "0 6px",
                                                                    }),
                                                                    indicatorsContainer: (base) => ({
                                                                        ...base,
                                                                        height: "25px",
                                                                    }),
                                                                    menuPortal: (base) => ({ ...base, zIndex: 2000 }),
                                                                    menu: (provided) => ({
                                                                        ...provided,
                                                                        fontSize: "12px",
                                                                    }),
                                                                }}
                                                            />

                                                        </td>

                                                        {/* Quantity */}
                                                        <td style={{ padding: "3px", textAlign: "right" }}>
                                                            <input
                                                                type="number"
                                                                value={item.Quantity}
                                                                name="Quantity"
                                                                onChange={(e) =>
                                                                    handleCalculation(
                                                                        e,
                                                                        qaName,
                                                                        workName,
                                                                        index,
                                                                        "Quantity"
                                                                    )
                                                                }
                                                                onClick={(e) => e.target.select()}
                                                                style={{
                                                                    width: "100%",
                                                                    textAlign: "right",
                                                                    border: "1px solid #bbb",
                                                                    borderRadius: "3px",
                                                                    padding: "2px",
                                                                }}
                                                            />
                                                        </td>

                                                        {/* Rate */}
                                                        <td style={{ padding: "3px", textAlign: "right" }}>
                                                            <input
                                                                type="number"
                                                                name="Rate"
                                                                value={item.Rate || 0}
                                                                onChange={(e) =>
                                                                    handleCalculation(
                                                                        e,
                                                                        qaName,
                                                                        workName,
                                                                        index,
                                                                        "Rate"
                                                                    )
                                                                }
                                                                onClick={(e) => e.target.select()}
                                                                style={{
                                                                    width: "100%",
                                                                    textAlign: "right",
                                                                    border: "1px solid #bbb",
                                                                    borderRadius: "3px",
                                                                    padding: "2px",
                                                                }}
                                                            />
                                                        </td>

                                                        {/* Amount */}
                                                        <td style={{ padding: "3px", textAlign: "right" }}>
                                                            <input
                                                                type="number"
                                                                value={item.Amount || 0}
                                                                disabled
                                                                style={{
                                                                    width: "100%",
                                                                    textAlign: "right",
                                                                    border: "1px solid #bbb",
                                                                    borderRadius: "3px",
                                                                    padding: "2px",
                                                                }}
                                                            />
                                                        </td>

                                                        {/* Taxable */}
                                                        <td style={{ padding: "3px", textAlign: "right" }}>
                                                            <input
                                                                type="number"
                                                                value={item.TaxableAmount || 0}
                                                                disabled
                                                                style={{
                                                                    width: "100%",
                                                                    textAlign: "right",
                                                                    border: "1px solid #bbb",
                                                                    borderRadius: "3px",
                                                                    padding: "2px",
                                                                }}
                                                            />
                                                        </td>

                                                        {/* GST & % */}
                                                        <td
                                                            style={{
                                                                padding: "2px",
                                                                textAlign: "right",
                                                                width: "110px",
                                                            }}
                                                        >
                                                            <input
                                                                value={
                                                                    parseFloat(item.CGSTAmount || 0) +
                                                                    parseFloat(item.SGSTAmount || 0) +
                                                                    parseFloat(item.IGSTAmount || 0)
                                                                }
                                                                disabled
                                                                style={{
                                                                    width: "70%",
                                                                    textAlign: "right",
                                                                    border: "1px solid #ccc",
                                                                    borderRadius: "3px",
                                                                    padding: "2px",
                                                                }}
                                                            />
                                                            <span
                                                                style={{
                                                                    marginLeft: "4px",
                                                                    color: "darkmagenta",
                                                                    fontSize: "11px",
                                                                }}
                                                            >
                                                                {item.GSTRate ? parseFloat(item.GSTRate) : 0}%
                                                            </span>
                                                        </td>

                                                        {/* Total */}
                                                        <td style={{ padding: "3px", textAlign: "right" }}>
                                                            <input
                                                                type="number"
                                                                value={item.TotalAmount || 0}
                                                                disabled
                                                                style={{
                                                                    width: "100%",
                                                                    textAlign: "right",
                                                                    border: "1px solid #bbb",
                                                                    borderRadius: "3px",
                                                                    padding: "2px",
                                                                }}
                                                            />
                                                        </td>

                                                        {/* Remarks */}
                                                        <td
                                                            style={{
                                                                fontSize: "11px",
                                                                fontWeight: "bold",
                                                                padding: "3px",
                                                                width: "15%",
                                                                borderRight: "1px solid #E0E0E0",
                                                            }}
                                                        >
                                                            <textarea
                                                                value={item.Remarks || ""}
                                                                rows={1}
                                                                name="Remarks"
                                                                onChange={(e) =>
                                                                    handleRemarksChange(e, qaName, workName, index)
                                                                }
                                                                style={{
                                                                    width: "100%",
                                                                    textAlign: "left",
                                                                    border: "1px solid #bbb",
                                                                    borderRadius: "3px",
                                                                    padding: "2px",
                                                                    resize: "vertical",
                                                                    fontSize: "11px",
                                                                    fontWeight: "bold",
                                                                }}
                                                            />
                                                        </td>

                                                        {/* Item Remark */}
                                                        <td
                                                            style={{
                                                                fontSize: "11px",
                                                                fontWeight: "bold",
                                                                padding: "3px",
                                                                width: "15%",
                                                                borderRight: "1px solid #E0E0E0",
                                                            }}
                                                        >
                                                            <textarea
                                                                value={item.ItemRemark || ""}
                                                                rows={1}
                                                                name="ItemRemark"
                                                                maxLength={ITEM_REMARK_MAX}
                                                                onChange={(e) =>
                                                                    handleItemRemarkChange(e, qaName, workName, index)
                                                                }
                                                                style={{
                                                                    width: "100%",
                                                                    textAlign: "left",
                                                                    border: `1px solid ${(item.ItemRemark || "").length >= ITEM_REMARK_MAX ? "#e53e3e" : "#bbb"}`,
                                                                    borderRadius: "3px",
                                                                    padding: "2px",
                                                                    resize: "vertical",
                                                                    fontSize: "11px",
                                                                    fontWeight: "bold",
                                                                }}
                                                            />
                                                            <div style={{
                                                                textAlign: "right",
                                                                fontSize: "10px",
                                                                color: (item.ItemRemark || "").length >= ITEM_REMARK_MAX ? "#e53e3e" : "#666",
                                                                marginTop: "1px",
                                                            }}>
                                                                {(item.ItemRemark || "").length}/{ITEM_REMARK_MAX}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                        </React.Fragment>
                                    ))}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}


        </div >

    );
};


export default QuotationCreate;
